import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import ButtonBase from '@mui/material/ButtonBase';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

type NavLink = {
  label: string;
  path: string;
};

type SeriesStep = {
  label: string;
  title?: string;
  path?: string;
  active?: boolean;
};

type Props = {
  title: string;
  seriesName: string;
  tags?: string[];
  seriesSteps?: SeriesStep[];
  prevLink?: NavLink;
  nextLink?: NavLink;
  children: React.ReactNode;
};

const SeriesStepper: React.FC<{ steps: SeriesStep[]; seriesName: string }> = ({
  steps,
  seriesName,
}) => {
  const navigate = useNavigate();
  const activeStep = steps.find((s) => s.active);
  const [hoveredTitle, setHoveredTitle] = useState<string | null>(null);

  const displayTitle = hoveredTitle ?? activeStep?.title ?? null;
  const isHovering = hoveredTitle !== null;

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        px: 1.5,
        py: 1,
        overflowX: 'auto',
      }}
    >
      {/* シリーズ名 + ステップボタン行 */}
      <Stack direction="row" alignItems="center" sx={{ minWidth: 'max-content' }}>
        <Typography
          variant="caption"
          fontWeight={700}
          color="text.primary"
          sx={{ whiteSpace: 'nowrap', mr: 0.5 }}
        >
          {seriesName}
        </Typography>
        <ChevronRightIcon sx={{ fontSize: 16, color: 'text.disabled', mx: 0.25, flexShrink: 0 }} />
        {steps.map((step, index) => (
          <React.Fragment key={step.label}>
            {step.path ? (
              <ButtonBase
                onClick={() => navigate(step.path!)}
                onMouseEnter={() => step.title && setHoveredTitle(step.title)}
                onMouseLeave={() => setHoveredTitle(null)}
                sx={{
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  border: step.active ? 2 : 1,
                  borderColor: step.active ? 'primary.main' : 'transparent',
                  transition: 'all 0.15s',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight={step.active ? 700 : 400}
                  color={step.active ? 'primary.main' : 'text.secondary'}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {step.label}
                </Typography>
              </ButtonBase>
            ) : (
              <Box
                onMouseEnter={() => step.title && setHoveredTitle(step.title)}
                onMouseLeave={() => setHoveredTitle(null)}
                sx={{
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  border: step.active ? 2 : 1,
                  borderColor: step.active ? 'primary.main' : 'transparent',
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight={step.active ? 700 : 400}
                  color={step.active ? 'primary.main' : 'text.disabled'}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {step.label}
                </Typography>
              </Box>
            )}
            {index < steps.length - 1 && (
              <ChevronRightIcon
                sx={{ fontSize: 16, color: 'text.disabled', mx: 0.25, flexShrink: 0 }}
              />
            )}
          </React.Fragment>
        ))}
      </Stack>

      {/* タイトル表示エリア */}
      {displayTitle && (
        <Box
          sx={{
            mt: 1,
            px: 1.5,
            py: 0.75,
            borderRadius: 1.5,
            bgcolor: isHovering ? 'action.hover' : 'transparent',
            borderColor: isHovering ? 'info.main' : 'primary.main',
            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          <Typography
            key={displayTitle}
            variant="body2"
            fontWeight={600}
            color={isHovering ? 'info.main' : 'primary.main'}
            sx={{
              animation: 'stepTitleFade 0.25s ease-out',
              '@keyframes stepTitleFade': {
                from: { opacity: 0, transform: 'translateX(-4px)' },
                to: { opacity: 1, transform: 'translateX(0)' },
              },
            }}
          >
            {displayTitle}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const HandsonArticleLayout: React.FC<Props> = ({
  title,
  seriesName,
  tags,
  seriesSteps,
  prevLink,
  nextLink,
  children,
}) => {
  const navigate = useNavigate();

  return (
    <Stack spacing={3} sx={{ maxWidth: 860, pb: 4 }}>
      {/* パンくず */}
      <Breadcrumbs separator={<NavigateNextIcon sx={{ fontSize: 16 }} />}>
        <Link
          component="button"
          variant="body2"
          underline="hover"
          color="text.secondary"
          onClick={() => navigate('/devdocs')}
          sx={{ cursor: 'pointer' }}
        >
          Dev Insights
        </Link>
        <Link
          component="button"
          variant="body2"
          underline="hover"
          color="text.secondary"
          onClick={() => navigate('/devdocs/handson')}
          sx={{ cursor: 'pointer' }}
        >
          AIと作る、壊れないバックエンド - 実践編
        </Link>
        <Typography variant="body2" color="text.primary">
          {seriesName}
        </Typography>
      </Breadcrumbs>

      {/* ヘッダー */}
      <Box>
        {tags && tags.length > 0 && (
          <Stack direction="row" spacing={0.5} sx={{ mb: 1.5 }}>
            {tags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                variant="outlined"
                sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600, borderRadius: 1 }}
              />
            ))}
          </Stack>
        )}
        <Typography variant="h5" fontWeight={700}>
          {title}
        </Typography>
      </Box>

      {/* シリーズ内ステッパー */}
      {seriesSteps && seriesSteps.length > 0 && (
        <SeriesStepper steps={seriesSteps} seriesName={seriesName} />
      )}

      <Divider />

      {/* コンテンツ */}
      <Stack spacing={3}>{children}</Stack>

      {/* 前後ナビゲーション */}
      {(prevLink || nextLink) && (
        <>
          <Divider />
          <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
            {prevLink ? (
              <ButtonBase
                onClick={() => navigate(prevLink.path)}
                sx={{
                  flex: 1,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 2,
                  justifyContent: 'flex-start',
                  transition: 'border-color 0.2s',
                  '&:hover': { borderColor: 'primary.main' },
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <ArrowBackIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Stack alignItems="flex-start">
                    <Typography variant="caption" color="text.disabled">
                      前の記事
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {prevLink.label}
                    </Typography>
                  </Stack>
                </Stack>
              </ButtonBase>
            ) : (
              <Box sx={{ flex: 1 }} />
            )}
            {nextLink ? (
              <ButtonBase
                onClick={() => navigate(nextLink.path)}
                sx={{
                  flex: 1,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 2,
                  textAlign: 'right',
                  transition: 'border-color 0.2s',
                  '&:hover': { borderColor: 'primary.main' },
                }}
              >
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  justifyContent="flex-end"
                  sx={{ width: '100%' }}
                >
                  <Stack alignItems="flex-end">
                    <Typography variant="caption" color="text.disabled">
                      次の記事
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {nextLink.label}
                    </Typography>
                  </Stack>
                  <ArrowForwardIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </Stack>
              </ButtonBase>
            ) : (
              <Box sx={{ flex: 1 }} />
            )}
          </Stack>
        </>
      )}
    </Stack>
  );
};

export default HandsonArticleLayout;
